import { isEmpty } from 'lodash-es';
import { FilterToolbarItemId } from 'src/components/Filters';
import { ExpandedEventData } from 'src/contexts/CatalogDataContext';
import { ErrorTypes } from 'src/contexts/ClientLogContext';
import {
  filterAndflattenListingGroup,
  flattenListingGroup,
} from 'src/modals/GroupListings/components/groupingUtils';
import {
  getPresetFromUiDateTimeRange,
  getUiDateTimeRangeFromPreset,
  InhandDateRangePresetNames,
} from 'src/utils/dateTimeUtils';
import { EmptyListingQuery } from 'src/utils/eventQueryUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  CatalogClient,
  DateTimeRange,
  GroupType,
  IListingGroupItem,
  Listing,
  ListingClient,
  ListingDetailDataField,
  ListingGroup,
  ListingMetrics,
  ListingMetricsInput,
  ListingQuery,
  ListingQueryV2,
  PosClientConfig,
  SectionalListingsForEventsInput,
} from 'src/WebApiController';

// Don't load sectional data for listing in invisible groups
const filterInvisibleGroups = (l: IListingGroupItem): boolean => {
  if (l == null) {
    return false;
  }
  if (l.isLtGrp) {
    return (l as ListingGroup).groupType !== GroupType.Invisible;
  }
  return true;
};

const deepOverrideListing = (
  listing: Listing,
  overrides: Record<number, Listing>,
  dataToOverride: ListingDetailDataField[]
) => {
  const groupItems = (listing as ListingGroup).groupItems;
  if (!isEmpty(groupItems)) {
    Object.assign(listing, {
      groupItems: groupItems.map((item) =>
        deepOverrideListing(item as Listing, overrides, dataToOverride)
      ),
    });
    return listing;
  }
  const override = overrides[listing.id];
  if (override) {
    if (
      dataToOverride.includes(ListingDetailDataField.Purchase) &&
      override.dataIsLoaded?.[ListingDetailDataField.Purchase]
    ) {
      Object.assign(listing, {
        isCurUserPo: override.isCurUserPo,
        poBys: override.poBys,
        poCreatedBys: override.poCreatedBys,
        poDate: override.poDate,
        poId: override.poId,
        poOrderId: override.poOrderId,
        poVendAccEmail: override.poVendAccEmail,
        dataIsLoaded: {
          ...listing.dataIsLoaded,
          [ListingDetailDataField.Purchase]: true,
        },
      } as Partial<Listing>);
    }

    if (
      dataToOverride.includes(ListingDetailDataField.Tags) &&
      override.dataIsLoaded?.[ListingDetailDataField.Tags]
    ) {
      Object.assign(listing, {
        tags: override.tags,
        dataIsLoaded: {
          ...listing.dataIsLoaded,
          [ListingDetailDataField.Tags]: true,
        },
      } as Partial<Listing>);
    }
  }
  return listing;
};

const mergeCatalogListings = (
  listings: Listing[],
  overrides: Listing[],
  dataToOverride: ListingDetailDataField[]
) => {
  if (overrides.length === 0 || dataToOverride.length === 0) {
    return listings;
  }

  const overrideById = overrides
    .flatMap(flattenListingGroup)
    .map((listing) => listing as Listing)
    .reduce(
      (map, listing) => {
        map[listing.id] = listing;
        return map;
      },
      {} as Record<number, Listing>
    );

  return listings.map((listing) =>
    deepOverrideListing(listing, overrideById, dataToOverride)
  );
};

export const getListingsSectionalDataForEvents = async (
  viagogoVirtualIds: string[],
  filterQuery: ListingQuery,
  dataToInclude: ListingDetailDataField[],
  {
    activeAccountWebClientConfig,
    onError,
  }: {
    activeAccountWebClientConfig: PosClientConfig;
    onError?: (error: ErrorTypes) => void;
  },
  currentData?: ExpandedEventData
) => {
  const emptyResults = viagogoVirtualIds.reduce((results, id) => {
    results[id] = {
      sales: null,
      salesSummary: null,
      listings: null,
      listingsSummary: null,
      ticketGroups: null,
      ticketGroupsSummary: null,
      failedToRetrieveData: false,
    };
    return results;
  }, {} as ExpandedEventData);
  const result = await tryInvokeApi(async () => {
    // Only process listing group when basic data is included
    const processListingGroup =
      currentData == null ||
      dataToInclude.includes(ListingDetailDataField.Basic);

    const input: SectionalListingsForEventsInput = {
      listingQuery: {
        ...filterQuery,
        // Is is to support the legacy event ID urls
        oldPosEventIds: viagogoVirtualIds
          .filter((id) => id.startsWith('old:'))
          .map((id) => id.substring(4)),
        eventOrMappingIds: viagogoVirtualIds.filter(
          (id) => !id.startsWith('old:')
        ),
      },
      curListings: null,
    };
    // No current data, we are loading for the first time
    if (currentData == null) {
      // ensure basic data is included
      dataToInclude.push(ListingDetailDataField.Basic);
      // Listing groups will be loaded in the first call
      const allListings = await new ListingClient(
        activeAccountWebClientConfig
      ).getListingsSectionalDataForEvents(
        input,
        Array.from(new Set(dataToInclude)),
        processListingGroup
      );
      return Promise.resolve(
        Object.entries(allListings).reduce((results, [id, listings]) => {
          results[id] = {
            sales: null,
            salesSummary: null,
            listings: listings.items,
            listingsSummary: listings.summary,
            ticketGroups: null,
            ticketGroupsSummary: null,
            failedToRetrieveData: false,
          };
          return results;
        }, emptyResults)
      );
    } else {
      const curListings = Object.entries(currentData ?? {})
        .flatMap(([_, data]) => data.listings ?? [])
        .flatMap(filterAndflattenListingGroup(filterInvisibleGroups))
        .map((listing) => listing as Listing)
        .map(
          (listing) =>
            ({
              id: listing.id,
              actions: listing.actions,
              seating: listing.seating,
              currency: listing.currency,
              viagVirtId: listing.viagVirtId,
              vldPurQty: listing.vldPurQty,
            }) as Listing
        );
      input.curListings = curListings;
      // Skip processing listing group in the follow up sectional calls
      const overrideListings = await new ListingClient(
        activeAccountWebClientConfig
      ).getListingsSectionalDataForEvents(
        input,
        dataToInclude,
        processListingGroup
      );
      return Promise.resolve(
        Object.entries(currentData).reduce((results, [id, data]) => {
          const override = overrideListings?.[id] ?? {
            items: [],
            summary: null,
          };
          results[id] = {
            ...data,
            listings: mergeCatalogListings(
              data.listings ?? [],
              override.items,
              dataToInclude
            ),
            listingsSummary:
              data.listingsSummary == null
                ? override.summary
                : override.summary == null
                  ? data.listingsSummary
                  : (deepOverrideListing(
                      data.listingsSummary,
                      {
                        [data.listingsSummary.id]: override.summary,
                      },
                      dataToInclude
                    ) as Listing),
          };
          return results;
        }, emptyResults)
      );
    }
  }, onError);

  if (result) {
    return Promise.resolve(result);
  }

  // Return current data if the call fails with current data
  return Promise.resolve(
    currentData ??
      viagogoVirtualIds.reduce((results, id) => {
        results[id] = {
          failedToRetrieveData: true,
          sales: null,
          salesSummary: null,
          listings: null,
          listingsSummary: null,
          ticketGroups: null,
          ticketGroupsSummary: null,
        };
        return results;
      }, {} as ExpandedEventData)
  );
};

export const getListingsForEvents = async (
  viagogoVirtualIds: string[],
  filterQuery: ListingQuery | ListingQueryV2,
  {
    activeAccountWebClientConfig,
    onError,
  }: {
    activeAccountWebClientConfig: PosClientConfig;
    onError?: (error: ErrorTypes) => void;
  },
  useReadonlyDB = false,
  doNotProcessListingGroups = false,
  includeInternalNotes = false,
  isForSearch = false,
  isForSearchV2 = false
) => {
  const result = await tryInvokeApi(async () => {
    const client = new ListingClient(activeAccountWebClientConfig);
    const filters = {
      ...filterQuery,
      // Is is to support the legacy event ID urls
      oldPosEventIds: viagogoVirtualIds
        .filter((id) => id.startsWith('old:'))
        .map((id) => id.substring(4)),
      eventOrMappingIds: viagogoVirtualIds.filter(
        (id) => !id.startsWith('old:')
      ),
    };

    const allListings = isForSearch
      ? isForSearchV2
        ? await client.searchListingsForEventsV2(
            filters as ListingQueryV2,
            useReadonlyDB,
            doNotProcessListingGroups,
            includeInternalNotes
          )
        : await client.searchListingsForEvents(
            filters,
            useReadonlyDB,
            doNotProcessListingGroups,
            includeInternalNotes
          )
      : await client.getListingsForEvents(
          filters,
          useReadonlyDB,
          doNotProcessListingGroups,
          includeInternalNotes
        );

    if (isForSearchV2) {
      // Search V2 only - if we have no sales for the given event ids, return null
      // to indicate that we failed to retrieve data for all the events
      const hasAnyListings = Object.values(allListings).some(
        (listings) => listings.items.length > 0
      );
      if (!hasAnyListings && viagogoVirtualIds.length > 0) {
        return Promise.resolve(null);
      }
    }

    const emptyResults = viagogoVirtualIds.reduce((results, id) => {
      results[id] = {
        sales: null,
        salesSummary: null,
        listings: null,
        listingsSummary: null,
        ticketGroups: null,
        ticketGroupsSummary: null,
        failedToRetrieveData: false,
      };
      return results;
    }, {} as ExpandedEventData);

    return Promise.resolve(
      Object.entries(allListings).reduce((results, [id, listings]) => {
        results[id] = {
          sales: null,
          salesSummary: null,
          listings: listings.items,
          listingsSummary: listings.summary,
          ticketGroups: null,
          ticketGroupsSummary: null,
          failedToRetrieveData: false,
          totalItemsCount: listings.totalItemsCount,
          pitId: listings.pitId,
        };
        return results;
      }, emptyResults)
    );
  }, onError);

  if (result) {
    return Promise.resolve(result);
  }

  return Promise.resolve(
    viagogoVirtualIds.reduce((results, id) => {
      results[id] = {
        failedToRetrieveData: true,
        sales: null,
        salesSummary: null,
        listings: null,
        listingsSummary: null,
        ticketGroups: null,
        ticketGroupsSummary: null,
      };
      return results;
    }, {} as ExpandedEventData)
  );
};

export const getCatalogMetrics = (
  client: CatalogClient,
  filterQuery: ListingQuery,
  rowVersion?: number | null,
  includeTags?: boolean
) => {
  return client.getCatalogMetricsForListings(
    filterQuery,
    rowVersion ?? undefined,
    includeTags
  );
};

export const minimizeListingQueryForMetrics = (
  filterQuery: ListingQuery
): ListingQuery => {
  return {
    // Deliberately using EmptyListingQuery - DefaultQuery or InitialQuery implicitly filters by Future Events
    ...EmptyListingQuery,
    pricerSellerUserId: filterQuery.pricerSellerUserId,
    purchaseBuyerId: filterQuery.purchaseBuyerId,
    purchaseOrderCreatedBy: filterQuery.purchaseOrderCreatedBy,
    isSeatSaver: filterQuery.isSeatSaver,
  };
};

export const getCatalogSummaryMetrics = (
  client: CatalogClient,
  metrics: ListingMetrics[],
  mergeCatalogMetrics: (metrics: ListingMetrics[]) => ListingMetrics[]
) => {
  const resultMetrics = mergeCatalogMetrics(metrics);
  if (resultMetrics?.length === 1) {
    return Promise.resolve(resultMetrics[0]);
  }
  return client.getSummaryMetricsForListings(
    resultMetrics.map(
      (m) =>
        ({
          totalTicketQuantity: m.tktQty,
          totalSoldQuantity: m.soldQty,
          totalUnsoldQuantity: m.unsoldQty,
          totalListedQuantity: m.listQty,
          totalUnlistedQuantity: m.unlistQty,
          currencyCode: m.currency,
          /** Total cost of all tickets */
          totalCost: m.ttlCst,
          /** Total cost of sold tickets */
          totalSoldCost: m.soldCst,
          /** Total cost of unsold tickets */
          totalUnsoldCost: m.unsoldCst,
          /** Total list price of all tickets */
          totalListPrice: m.ttlListPrc,
          /** Total value of all listed tickets */
          // This is un-used and only exists for backward compatibility
          totalListedValue: 0,
          /** Total list price of unsold tickets */
          totalUnsoldListPrice: m.ttlUnsoldListPrc,
          /** Total net proceeds of sold tickets */
          totalNetProceeds: m.netProcs,
          /** Total gross profit of sold tickets */
          totalGrossProfit: m.pandL,
        }) as ListingMetricsInput
    )
  );
};

export const getEventDetailedMetrics = (
  client: CatalogClient,
  filterQuery: ListingQuery,
  rowVersion?: number | null
) => client.getCatalogDetailedMetricsForListings(filterQuery, rowVersion);

export const listingQueryValueTransformFromUrl = (
  key: string,
  value: string
) => {
  if (!value) {
    return value;
  }

  const listingsKey = key as keyof ListingQuery;
  if (listingsKey === 'inHandDates') {
    const preset = getUiDateTimeRangeFromPreset(
      value,
      InhandDateRangePresetNames
    );
    if (preset) {
      return preset;
    }
  }

  return value;
};

export const listingQueryValueTransformToUrl = (
  key: FilterToolbarItemId,
  value: unknown
) => {
  if (!value) {
    return value;
  }

  const listingsKey = key as keyof ListingQuery;

  if (listingsKey === 'inHandDates') {
    const preset = getPresetFromUiDateTimeRange(value as DateTimeRange);
    if (preset && InhandDateRangePresetNames.includes(preset.name)) {
      return preset.name;
    }
  }

  return value;
};
